import { useState } from 'react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Input } from '@finn-auto/design-system/atoms/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@finn-auto/design-system/atoms/select';
import { showToast } from '@finn-auto/design-system/atoms/toast';
import { Building2, Save, UserRound } from 'lucide-react';
import { cn } from '../../../shared';
import { VariableTextarea } from '../components/VariableTextarea';
import { TemplateBodyEditors } from '../components/TemplateBodyEditors';
import { SlackFormattingHint } from './SlackFormattingHint';
import { EmailFormattingHint } from './EmailFormattingHint';
import {
  CATEGORY_LABELS,
  TOKENS_BY_CATEGORY,
  isInternalCategory,
  isSnippetCategory,
  isSubjectlessOutreach,
  saveEmailTemplate,
  type Category,
  type EmailTemplate,
  type Scope,
} from '../services/emailTemplatesApi';

const slugify = (s: string) =>
  s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60);

/** A slug from the label, made unique against keys already used in this scope+category. */
export function uniqueKey(label: string, existingKeys: string[]): string {
  const base = slugify(label) || 'template';
  const set = new Set(existingKeys);
  if (!set.has(base)) return base;
  let i = 2;
  while (set.has(`${base}-${i}`)) i += 1;
  return `${base}-${i}`;
}

export function ScopeChip({ scope }: { scope: Scope }) {
  const co = scope === 'company';
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium',
        co ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
      )}
    >
      {co ? <Building2 className="h-3 w-3" /> : <UserRound className="h-3 w-3" />}
      {co ? 'Company' : 'Personal'}
    </span>
  );
}

const NO_TEMPLATE = '__blank__';

/**
 * Draft a new template — blank, or (for a personal template) pre-filled from a
 * company one so a recruiter can "refine & save as personal". The key is derived
 * from the label, so there is no slug to type. Category is fixed by the section.
 */
export function NewTemplateForm({
  scope,
  category,
  existingKeys,
  startFrom,
  initialSeedId,
  onSaved,
  onCancel,
}: {
  scope: Scope;
  category: Category;
  existingKeys: string[];
  /** Company templates (this category) a personal one may be seeded from. */
  startFrom: EmailTemplate[];
  /** Pre-select this company template (from "Refine as personal"). */
  initialSeedId?: string | null;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const seed = initialSeedId ? startFrom.find((t) => t.id === initialSeedId) : undefined;
  const [basedOn, setBasedOn] = useState<string>(seed ? seed.id : NO_TEMPLATE);
  const [label, setLabel] = useState(seed ? `${seed.label} (mine)` : '');
  const [subjectDe, setSubjectDe] = useState(seed ? seed.subjectDe : '');
  const [subjectEn, setSubjectEn] = useState(seed ? seed.subjectEn : '');
  const [de, setDe] = useState(seed ? seed.de : '');
  const [en, setEn] = useState(seed ? seed.en : '');
  const [busy, setBusy] = useState(false);
  const tokens = TOKENS_BY_CATEGORY[category];
  /**
   * Internal templates are Slack messages to colleagues, so two things differ: Slack has no
   * subject, and it uses mrkdwn rather than HTML. BOTH languages are real — several hiring teams
   * collaborate in German, and the sender picks the language from the role.
   */
  const internal = isInternalCategory(category);
  /*
    A one-line snippet has no subject either — the booking PAGE is not an email. Same field is
    hidden for both, but for different reasons, so they stay two predicates rather than one
    "hasNoSubject": Slack has no subject line, and a sentence on a web page has nowhere to put one.
  */
  const noSubject = internal || isSnippetCategory(category) || isSubjectlessOutreach(category);

  function applyTemplate(id: string) {
    setBasedOn(id);
    const src = startFrom.find((t) => t.id === id);
    if (!src) return;
    setLabel(`${src.label} (mine)`);
    setSubjectDe(src.subjectDe);
    setSubjectEn(src.subjectEn);
    setDe(src.de);
    setEn(src.en);
  }

  const canSave = !busy && !!label.trim() && (!!de.trim() || !!en.trim());

  async function create() {
    setBusy(true);
    try {
      const key = uniqueKey(label, existingKeys);
      await saveEmailTemplate({
        key,
        scope,
        category,
        label: label.trim(),
        order: 0,
        subjectDe: noSubject ? '' : subjectDe,
        subjectEn: noSubject ? '' : subjectEn,
        de,
        en,
        active: true,
      });
      showToast({ status: 'success', description: `Saved “${label.trim()}”.` });
      onSaved();
    } catch (e) {
      showToast({ status: 'error', description: e instanceof Error ? e.message : 'Could not save the template.' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mb-2 rounded-2xl border border-[#B9D9FB] bg-[#DBEBFC]/50 p-3">
      <div className="mb-2 flex flex-wrap items-center gap-2 text-xs text-gray-500">
        <ScopeChip scope={scope} />
        <span className="rounded-full bg-white px-2.5 py-1 font-medium text-gray-600">
          {CATEGORY_LABELS[category]}
        </span>
        <span>· new template</span>
      </div>

      {scope === 'personal' && startFrom.length > 0 && (
        <div className="mb-2">
          <Select value={basedOn} onValueChange={applyTemplate}>
            <SelectTrigger label="Start from" className="w-full rounded-xl">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_TEMPLATE}>Blank — write my own</SelectItem>
              {startFrom.map((t) => (
                <SelectItem key={t.id} value={t.id}>
                  Company: {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="mt-1 text-[11px] text-gray-400">
            Pick a company template to copy its text here, then edit it freely.
          </p>
        </div>
      )}

      <Input
        label="Label"
        value={label}
        onChange={(e) => setLabel((e.target as HTMLInputElement).value)}
        className="rounded-xl"
      />
      {!noSubject && (
        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          <Input label="Subject (DE)" value={subjectDe} onChange={(e) => setSubjectDe((e.target as HTMLInputElement).value)} className="rounded-xl" />
          <Input label="Subject (EN)" value={subjectEn} onChange={(e) => setSubjectEn((e.target as HTMLInputElement).value)} className="rounded-xl" />
        </div>
      )}
      {category === 'outreach-inmail' && (
        <p className="m-0 mt-1 text-xs text-gray-500">Leave the subject empty to use the standard subject from the InMail Library.</p>
      )}
      <div className="mt-2 space-y-2">
        {noSubject ? (
          <>
            <VariableTextarea label="German" tokens={tokens} rows={6} value={de} onChange={setDe} />
            <VariableTextarea label="English" tokens={tokens} rows={6} value={en} onChange={setEn} />
          </>
        ) : (
          <TemplateBodyEditors tokens={tokens} rows={6} de={de} en={en} setDe={setDe} setEn={setEn} />
        )}
        {internal && <SlackFormattingHint />}
        {!noSubject && <EmailFormattingHint />}
      </div>
      <div className="mt-2 flex items-center justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onCancel} disabled={busy} className="rounded-full">
          Cancel
        </Button>
        <Button size="sm" onClick={create} disabled={!canSave} className="rounded-full">
          <Save className="mr-1 h-3.5 w-3.5" /> {busy ? 'Saving…' : 'Save template'}
        </Button>
      </div>
      {!canSave && !busy && (
        <p className="mt-1 text-right text-[11px] text-gray-400">Add a label and some text to save.</p>
      )}
    </div>
  );
}
